"use client";

import { FC, useMemo } from "react";
import { GridCountRange } from "@types";

import { Grid } from "components/organisms/Grid/Grid";
import { Container } from "components/organisms/Container/Container";
import { Spacer } from "components/organisms/Spacer/Spacer";
import { isEmptyDocument } from "datocms-structured-text-utils";

import { Pretitle } from "components/atoms/Pretitle/Pretitle";

import { GridSectionRecord } from "@graphql/types/graphql";
import { ImageBlock } from "components/InlineBlocks/ImageBlock";
import { Video } from "components/InlineBlocks/Video";
import { ChecklistItem } from "components/InlineBlocks/ChecklistItem";
import { CBAccordion } from "components/InlineBlocks/CBAccordion";
import { Quote } from "components/InlineBlocks/Quote";
import { StructuredText } from "components/sections/RichtextSection/StructuredText";
import { Statistic } from "./_components/Statistic";
import { GridTeaser } from "./_components/GridTeaser";

import s from "./GridSection.module.scss";
import { BrandedImageTeaser } from "./_components/BrandedImageTeaser";

interface Props {
  data: GridSectionRecord;
}

export const GridSection: FC<Props> = ({
  data: {
    pretitle,
    title,
    description,
    items,
    columnCount = "2",
    border,
    hoverEffect,
    enableBackground,
  },
}) => {
  const background = !!enableBackground;

  const onlyHasImages = useMemo(() => {
    const imageItems = items.filter((item) => item.__typename === "ImageRecord");

    return imageItems.length === items.length;
  }, [items]);

  const hasVideo = useMemo(() => {
    const videoItems = items.filter((item) => item.__typename === "VideoRecord");

    return videoItems.length > 0;
  }, [items]);

  const gridContent = useMemo(
    () => (
      <>
        <div className={s.Inner}>
          {(pretitle || title) && (
            <Pretitle pretitle={pretitle || undefined} title={title || undefined} />
          )}
          {description && !isEmptyDocument(description) && (
            <div className={s.Description}>
              <StructuredText structuredText={description as any} />
            </div>
          )}
        </div>
        <Grid
          columns={columnCount as GridCountRange}
          largeGap={hasVideo && columnCount === "2"}
          hasBorder={!!border}
          hasHoverEffect={!!hoverEffect}
        >
          {items.map((item) => {
            switch (item.__typename) {
              case "TeaserRecord":
                return (
                  <GridTeaser
                    data={item}
                    columnCount={columnCount as GridCountRange}
                    background={background}
                    hasBorder={!border}
                    hasHoverEffect={!!hoverEffect}
                    key={item.id}
                  />
                );
              case "ImageRecord":
                return (
                  <div key={item.id}>
                    <ImageBlock data={item} isSmall={!onlyHasImages} />
                  </div>
                );
              case "AccordionRecord":
                return (
                  <div key={item.id}>
                    <CBAccordion {...item} />
                  </div>
                );
              case "QuoteRecord":
                return (
                  <div key={item.id} className={s.VerticalCenter}>
                    <Quote data={item} />
                  </div>
                );
              case "VideoRecord":
                return (
                  <div key={item.id}>
                    <Video data={item} showInModal />
                  </div>
                );
              case "StatisticRecord":
                return (
                  <div key={item.id}>
                    <Statistic data={item} />
                  </div>
                );
              case "ChecklistItemRecord":
                return (
                  <div key={item.id}>
                    <ChecklistItem data={item} />
                  </div>
                );
              case "BrandedImageTeaserRecord":
                return (
                  <div key={item.id}>
                    <BrandedImageTeaser data={item} />
                  </div>
                );
              default:
                return null;
            }
          })}
        </Grid>
      </>
    ),
    [
      border,
      columnCount,
      items,
      pretitle,
      title,
      onlyHasImages,
      hasVideo,
      hoverEffect,
      description,
      enableBackground,
    ],
  );

  if (pretitle || title) {
    return (
      <Container>
        <Spacer size="l">{gridContent}</Spacer>
      </Container>
    );
  }

  return <Container>{gridContent}</Container>;
};
